import { useEffect, useState } from 'react';
import { api, resolveImageUrl } from '../lib/api.js';

interface Product {
  sku: string;
  nickname: string | null;
  asin: string | null;
  title: string | null;
  imageUrl: string | null;
  brand: string | null;
  cogs: number | null;
  isMonitored: boolean;
  bsr: number | null;
  monthlySold: number | null;
  parentAsin: string | null;
  parentNickname: string | null;
  category: string | null;
  notes: string | null;
  marketplaces: string[];
}

interface ParentGroup {
  parentAsin: string;
  parentNickname: string;
  category: string | null;
  children: Product[];
}

const MARKETPLACE_LABELS: Record<string, string> = {
  ATVPDKIKX0DER: 'US', A2EUQ1WTGCTBG2: 'CA', A1AM78C64UM0Y8: 'MX',
  A1F83G8C2ARO7P: 'UK', A1PA6795UKMFR9: 'DE', A13V1IB3VIYZZH: 'FR',
  APJ6JRA9NG5V4: 'IT', A1RKKUPIHCS9HS: 'ES', A1805IZSGTT6HS: 'NL', A2NODRKZP88ZB9: 'SE',
};

function buildGroups(products: Product[], parentNames: Record<string, string>): ParentGroup[] {
  const groups = new Map<string, ParentGroup>();
  const orphans: Product[] = [];
  for (const p of products) {
    if (p.parentAsin) {
      if (!groups.has(p.parentAsin)) {
        groups.set(p.parentAsin, {
          parentAsin: p.parentAsin,
          parentNickname: p.parentNickname ?? parentNames[p.parentAsin] ?? p.parentAsin,
          category: p.category,
          children: [],
        });
      }
      groups.get(p.parentAsin)!.children.push(p);
    } else {
      orphans.push(p);
    }
  }
  for (const p of orphans) {
    const key = p.asin ?? p.sku;
    groups.set(key, {
      parentAsin: key,
      parentNickname: parentNames[key] ?? p.nickname ?? p.title?.slice(0, 40) ?? p.sku,
      category: p.category,
      children: [p],
    });
  }
  return [...groups.values()].sort((a, b) => b.children.length - a.children.length);
}

// Inline editable field
function EditableField({ value, onSave, placeholder, className = '' }: {
  value: string; onSave: (v: string) => void; placeholder?: string; className?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  if (editing) {
    return (
      <input type="text" value={draft} onChange={e => setDraft(e.target.value)} autoFocus
        placeholder={placeholder}
        className={`rounded border border-brand-300 px-2 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500 ${className}`}
        onBlur={() => { onSave(draft); setEditing(false); }}
        onKeyDown={e => {
          if (e.key === 'Enter') { onSave(draft); setEditing(false); }
          if (e.key === 'Escape') { setDraft(value); setEditing(false); }
        }} />
    );
  }
  return (
    <button onClick={() => { setDraft(value); setEditing(true); }}
      className={`text-left hover:text-brand-600 transition-colors ${className}`}
      title="Click to edit">
      {value || <span className="text-gray-300 italic">{placeholder ?? 'Click to set'}</span>}
    </button>
  );
}

export function Products() {
  const [products, setProducts] = useState<Product[]>([]);
  const [parentNames, setParentNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedParent, setExpandedParent] = useState<string | null>(null);
  const [view, setView] = useState<'grouped' | 'flat'>('grouped');

  useEffect(() => {
    api.get<{ products: Product[]; parentNames: Record<string, string> }>('/inventory/products')
      .then(res => { setProducts(res.products); setParentNames(res.parentNames ?? {}); })
      .finally(() => setLoading(false));
  }, []);

  async function updateField(sku: string, field: string, value: string | number | null) {
    await api.put(`/inventory/local-products/${encodeURIComponent(sku)}`, { [field]: value || null });
    setProducts(prev => prev.map(p => p.sku === sku ? { ...p, [field]: value || null } : p));
  }

  // Update parent ASIN name — stored separately, does NOT touch child nicknames
  async function updateParentNickname(parentAsin: string, newNickname: string) {
    await api.patch(`/inventory/parent-asin/${encodeURIComponent(parentAsin)}/nickname`, { nickname: newNickname });
    setParentNames(prev => ({ ...prev, [parentAsin]: newNickname }));
  }

  const filtered = products.filter(p => {
    if (!search) return true;
    const s = search.toLowerCase();
    return p.sku.toLowerCase().includes(s) || p.nickname?.toLowerCase().includes(s)
      || p.asin?.toLowerCase().includes(s) || p.title?.toLowerCase().includes(s)
      || p.brand?.toLowerCase().includes(s) || p.parentAsin?.toLowerCase().includes(s)
      || p.category?.toLowerCase().includes(s);
  });

  const groups = buildGroups(filtered, parentNames);

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-10 w-64 rounded-lg bg-gray-200" />
        {[1, 2, 3].map(i => <div key={i} className="h-24 rounded-xl bg-gray-200" />)}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold">Product Directory</h2>
          <p className="text-sm text-gray-500">
            {products.length} products in {buildGroups(products, parentNames).length} product families. Click any field to edit.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-1 rounded-lg border border-gray-200 p-0.5">
            <button onClick={() => setView('grouped')}
              className={`rounded px-2.5 py-1 text-xs font-medium ${view === 'grouped' ? 'bg-brand-50 text-brand-700' : 'text-gray-500'}`}>
              Grouped</button>
            <button onClick={() => setView('flat')}
              className={`rounded px-2.5 py-1 text-xs font-medium ${view === 'flat' ? 'bg-brand-50 text-brand-700' : 'text-gray-500'}`}>
              Flat</button>
          </div>
          <input type="text" placeholder="Search SKU, ASIN, name, category..."
            value={search} onChange={e => setSearch(e.target.value)}
            className="w-64 rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500" />
        </div>
      </div>

      {view === 'grouped' ? (
        /* ── Grouped by Parent ASIN ──────────────────────────────── */
        <div className="space-y-3">
          {groups.map(g => {
            const isExpanded = expandedParent === g.parentAsin;
            const firstChild = g.children[0];
            const thumb = firstChild?.imageUrl;

            return (
              <div key={g.parentAsin} className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                {/* Parent row */}
                <div className="flex items-center gap-4 px-5 py-4 cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpandedParent(isExpanded ? null : g.parentAsin)}>
                  {/* Image */}
                  <div className="h-14 w-14 flex-shrink-0 rounded-lg border border-gray-100 bg-gray-50 overflow-hidden">
                    {thumb ? (
                      <img src={resolveImageUrl(thumb) ?? ''} alt="" className="h-full w-full object-contain" loading="lazy" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-gray-300">
                        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z" />
                        </svg>
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-900"
                        onClick={e => e.stopPropagation()}>
                        <EditableField value={g.parentNickname}
                          onSave={v => updateParentNickname(g.parentAsin, v)}
                          placeholder="Set parent name..."
                          className="text-sm font-semibold text-gray-900" />
                      </span>
                      {g.category && (
                        <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-600">
                          {g.category}
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 flex items-center gap-3 text-xs text-gray-500">
                      <span className="font-mono">Parent: {g.parentAsin}</span>
                      <span>{g.children.length} variation{g.children.length !== 1 ? 's' : ''}</span>
                      {firstChild?.brand && <span>{firstChild.brand}</span>}
                    </div>
                  </div>

                  {/* Marketplace badges (all unique across children) */}
                  <div className="flex flex-shrink-0 gap-1">
                    {[...new Set(g.children.flatMap(c => c.marketplaces))].map(mp => (
                      <span key={mp} className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
                        {MARKETPLACE_LABELS[mp] ?? mp.slice(0, 3)}
                      </span>
                    ))}
                  </div>

                  {/* Chevron */}
                  <svg className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>

                {/* Children (variations) */}
                {isExpanded && (
                  <div className="border-t border-gray-100 divide-y divide-gray-50">
                    {g.children.map(p => (
                      <div key={p.sku} className={`flex items-center gap-4 px-5 py-3 bg-gray-50/50 ${!p.isMonitored ? 'opacity-50' : ''}`}>
                        {/* Thumbnail */}
                        <div className="h-10 w-10 flex-shrink-0 rounded border border-gray-100 bg-white overflow-hidden ml-4">
                          {p.imageUrl ? (
                            <img src={resolveImageUrl(p.imageUrl) ?? ''} alt="" className="h-full w-full object-contain" loading="lazy" />
                          ) : (
                            <div className="h-full w-full bg-gray-50" />
                          )}
                        </div>

                        {/* Variation info */}
                        <div className="flex-1 min-w-0 space-y-1">
                          <div className="flex items-center gap-2">
                            <EditableField value={p.nickname ?? ''}
                              onSave={v => updateField(p.sku, 'nickname', v)}
                              placeholder="Variation nickname..."
                              className="text-sm font-medium text-gray-800" />
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span className="font-mono">{p.sku}</span>
                            {p.asin && <span className="font-mono text-gray-400">{p.asin}</span>}
                            <span onClick={e => e.stopPropagation()}>
                              <EditableField value={p.brand ?? ''}
                                onSave={v => updateField(p.sku, 'brand', v)}
                                placeholder="Brand"
                                className="text-xs text-gray-500" />
                            </span>
                            <span onClick={e => e.stopPropagation()}>
                              <EditableField value={p.category ?? ''}
                                onSave={v => updateField(p.sku, 'category', v)}
                                placeholder="Category"
                                className="text-xs text-gray-500" />
                            </span>
                          </div>
                          {p.title && p.title !== p.nickname && (
                            <p className="text-[10px] text-gray-400 truncate max-w-[400px]">{p.title}</p>
                          )}
                        </div>

                        {/* Editable fields */}
                        <div className="flex items-center gap-4 flex-shrink-0 text-xs">
                          <div className="text-center">
                            <div className="text-[10px] uppercase text-gray-400">COGS</div>
                            <EditableField value={p.cogs != null ? `$${p.cogs.toFixed(2)}` : ''}
                              onSave={v => {
                                const num = parseFloat(v.replace(/[^0-9.]/g, ''));
                                if (!isNaN(num)) updateField(p.sku, 'cogs', num);
                              }}
                              placeholder="$0.00"
                              className="text-xs font-medium text-gray-700 w-16 text-center" />
                          </div>
                          {p.bsr != null && (
                            <div className="text-center">
                              <div className="text-[10px] uppercase text-gray-400">BSR</div>
                              <div className="text-xs font-medium">#{p.bsr.toLocaleString()}</div>
                            </div>
                          )}
                          {p.monthlySold != null && (
                            <div className="text-center">
                              <div className="text-[10px] uppercase text-gray-400">Monthly</div>
                              <div className="text-xs font-medium">{p.monthlySold}</div>
                            </div>
                          )}
                        </div>

                        {/* Marketplace badges */}
                        <div className="flex flex-shrink-0 gap-1">
                          {p.marketplaces.map(mp => (
                            <span key={mp} className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
                              {MARKETPLACE_LABELS[mp] ?? mp.slice(0, 3)}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        /* ── Flat view ───────────────────────────────────────────── */
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                <th className="px-4 py-3">Product</th>
                <th className="px-4 py-3">SKU</th>
                <th className="px-4 py-3">ASIN</th>
                <th className="px-4 py-3">Parent ASIN</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Brand</th>
                <th className="px-4 py-3 text-right">COGS</th>
                <th className="px-4 py-3">Markets</th>
                <th className="px-4 py-3">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(p => (
                <tr key={p.sku} className={`hover:bg-gray-50 ${!p.isMonitored ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      {p.imageUrl ? (
                        <img src={resolveImageUrl(p.imageUrl) ?? ''} alt="" className="h-8 w-8 rounded border object-contain" loading="lazy" />
                      ) : (
                        <div className="h-8 w-8 rounded border bg-gray-50" />
                      )}
                      <EditableField value={p.nickname ?? ''}
                        onSave={v => updateField(p.sku, 'nickname', v)}
                        placeholder="Nickname..."
                        className="text-sm font-medium text-gray-800" />
                    </div>
                  </td>
                  <td className="px-4 py-2 font-mono text-xs text-gray-500">{p.sku}</td>
                  <td className="px-4 py-2 font-mono text-xs text-gray-400">{p.asin}</td>
                  <td className="px-4 py-2 font-mono text-xs text-gray-400">
                    <EditableField value={p.parentAsin ?? ''}
                      onSave={v => updateField(p.sku, 'parent_asin', v)}
                      placeholder="Parent ASIN"
                      className="font-mono text-xs text-gray-400" />
                  </td>
                  <td className="px-4 py-2 text-xs">
                    <EditableField value={p.category ?? ''}
                      onSave={v => updateField(p.sku, 'category', v)}
                      placeholder="Category"
                      className="text-xs text-gray-600" />
                  </td>
                  <td className="px-4 py-2 text-xs">
                    <EditableField value={p.brand ?? ''}
                      onSave={v => updateField(p.sku, 'brand', v)}
                      placeholder="Brand"
                      className="text-xs text-gray-600" />
                  </td>
                  <td className="px-4 py-2 text-right text-xs">
                    <EditableField value={p.cogs != null ? `$${p.cogs.toFixed(2)}` : ''}
                      onSave={v => {
                        const num = parseFloat(v.replace(/[^0-9.]/g, ''));
                        if (!isNaN(num)) updateField(p.sku, 'cogs', num);
                      }}
                      placeholder="$0.00"
                      className="text-xs text-gray-700" />
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex gap-1">
                      {p.marketplaces.map(mp => (
                        <span key={mp} className="rounded bg-gray-100 px-1 py-0.5 text-[10px] text-gray-600">
                          {MARKETPLACE_LABELS[mp] ?? mp.slice(0, 2)}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-xs max-w-[150px]">
                    <EditableField value={p.notes ?? ''}
                      onSave={v => updateField(p.sku, 'notes', v)}
                      placeholder="Notes..."
                      className="text-xs text-gray-500 truncate" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {filtered.length === 0 && (
        <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">
          {search ? 'No products match your search.' : 'No products yet. Run a catalog sync first.'}
        </div>
      )}
    </div>
  );
}
